import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
// Camera functionality implemented with native browser API
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format } from 'date-fns';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Auth from './components/Auth';
import SubscriptionManager from './components/SubscriptionManager';
import FeedbackButton from './components/FeedbackButton';
import './App.css';

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './components/ui/dropdown-menu';
import { toast } from './hooks/use-toast';
import { Toaster } from './components/ui/toaster';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Zoomable Image Component
const ZoomableImage = ({ src, alt, className }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div className="border rounded-lg overflow-hidden bg-gray-50 relative">
      {!imageError ? (
        <TransformWrapper
          initialScale={1}
          minScale={0.5}
          maxScale={3}
          wheel={{ step: 0.1 }}
          pinch={{ step: 5 }}
          doubleClick={{ mode: "reset" }}
          centerOnInit={true}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              {/* Zoom Controls */}
              <div className="absolute top-2 right-2 z-10 flex flex-col space-y-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => zoomIn()}
                  className="bg-white/90 hover:bg-white text-gray-700 h-8 w-8 p-0"
                  title="Zoom In"
                >
                  +
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => zoomOut()}
                  className="bg-white/90 hover:bg-white text-gray-700 h-8 w-8 p-0"
                  title="Zoom Out"
                >
                  -
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resetTransform()}
                  className="bg-white/90 hover:bg-white text-gray-700 h-8 px-2 text-xs"
                  title="Reset"
                >
                  Reset
                </Button>
              </div>
              
              <TransformComponent>
                <img
                  src={src}
                  alt={alt}
                  className={`max-w-full h-auto ${className}`}
                  onError={() => setImageError(true)}
                  style={{ cursor: 'grab' }}
                  onMouseDown={(e) => e.target.style.cursor = 'grabbing'}
                  onMouseUp={(e) => e.target.style.cursor = 'grab'}
                />
              </TransformComponent>
              
              {/* Instructions */}
              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                Double-click to reset • Scroll to zoom • Drag to pan
              </div>
            </>
          )}
        </TransformWrapper>
      ) : (
        <div className="p-8 text-center text-gray-500">
          <div className="text-4xl mb-2">📄</div>
          <p>Receipt image not available</p>
        </div>
      )}
    </div>
  );
};

// Navigation Component with Mobile Responsive Menu
const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navLinks = [
    { to: '/', label: 'Dashboard' },
    { to: '/receipts', label: 'All Receipts' },
    { to: '/items', label: 'Itemized Items' },
    { to: '/upload', label: 'Upload Receipt' }
  ];
  
  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  
  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Original Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
              SnapStax
            </div>
            <div className="text-xs text-gray-500 hidden sm:block">by summarygpt.com</div>
          </Link>

          {/* Desktop Navigation - Pill Tabs with Animation */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link 
                key={link.to}
                to={link.to} 
                className={`relative px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 ease-in-out ${
                  isActiveLink(link.to) 
                    ? 'text-white' 
                    : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                {isActiveLink(link.to) && (
                  <span className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-blue-600 rounded-full -z-10 animate-in fade-in zoom-in duration-300" />
                )}
                {link.label}
              </Link>
            ))}
          </div>
          
          {/* Desktop User Dropdown Menu */}
          <div className="hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center space-x-2 hover:bg-gray-50 rounded-full px-3 py-2 transition-all duration-200">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-600 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                    {getInitials(user?.full_name)}
                  </div>
                  <span className="text-sm font-medium text-gray-700">{user?.full_name || 'User'}</span>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-emerald-600 mt-1 capitalize">{user?.subscription_tier || 'Free'} Plan</p>
                </div>
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/subscription')} className="cursor-pointer">
                  Subscription
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600">
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-600 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-xs">
              {getInitials(user?.full_name)}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2"
            >
              <div className="w-6 h-6 flex flex-col justify-center space-y-1.5">
                <div className={`w-6 h-0.5 bg-gray-600 transition-transform duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></div>
                <div className={`w-6 h-0.5 bg-gray-600 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`}></div>
                <div className={`w-6 h-0.5 bg-gray-600 transition-transform duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></div>
              </div>
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* User Info */}
              <div className="px-3 py-3 bg-gray-50 rounded-lg mb-3">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
                <p className="text-xs text-emerald-600 mt-1 capitalize">{user?.subscription_tier || 'Free'} Plan</p>
              </div>
              
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActiveLink(link.to)
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              <Link
                to="/profile"
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActiveLink('/profile')
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Profile Settings
              </Link>
              
              <div className="border-t border-gray-200 mt-3 pt-3">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Receipt Review Component
const ReceiptReview = ({ receipt, onSave, onCancel, loading }) => {
  const [editedReceipt, setEditedReceipt] = useState(receipt);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  useEffect(() => {
    // Check for duplicates when component loads
    checkForDuplicates();
  }, []);

  const checkForDuplicates = async () => {
    try {
      const response = await axios.post(`${API}/receipts/check-duplicate`, {
        vendor_name: receipt.vendor_name,
        total_amount: receipt.total_amount,
        date: receipt.date,
        invoice_number: receipt.invoice_number
      });
      
      if (response.data.is_potential_duplicate) {
        setDuplicateWarning(response.data);
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedReceipt(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    setEditedReceipt(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addNewItem = () => {
    const newItem = {
      id: Date.now().toString(),
      product_name: '',
      unit_price: 0,
      quantity: 1,
      total_price: 0,
      is_taxable: null,
      tax_indicator: null,
      tax_amount: null,
      category: null
    };
    setEditedReceipt(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index) => {
    setEditedReceipt(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Review & Edit Receipt</h1>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex">
            <div className="text-amber-600 mr-3">⚠️</div>
            <div>
              <h3 className="text-sm font-medium text-amber-800">AI Parsing Notice</h3>
              <p className="text-sm text-amber-700 mt-1">
                The information below was extracted using AI and may contain errors. Please review and correct any inaccuracies before saving.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Duplicate Warning */}
      {duplicateWarning && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="text-red-600 mr-3">🚨</div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Potential Duplicate Detected</h3>
              <p className="text-sm text-red-700 mt-1">{duplicateWarning.message}</p>
              <p className="text-sm text-red-700">Do you want to proceed anyway?</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Side - Receipt Image */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Original Receipt</h3>
          <div className="h-96">
            <ZoomableImage
              src={`${BACKEND_URL}/api/receipts/${receipt.id}/file`}
              alt="Receipt"
              className="max-h-96 object-contain"
            />
          </div>
        </div>

        {/* Right Side - Editable Fields */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">Receipt Information</h3>
          
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
              <input
                type="text"
                value={editedReceipt.invoice_number || ''}
                onChange={(e) => handleFieldChange('invoice_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter invoice number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
              <input
                type="text"
                value={editedReceipt.vendor_name || ''}
                onChange={(e) => handleFieldChange('vendor_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter vendor name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={editedReceipt.date || ''}
                onChange={(e) => handleFieldChange('date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal (Before Tax)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editedReceipt.subtotal || ''}
                  onChange={(e) => handleFieldChange('subtotal', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editedReceipt.total_amount || ''}
                  onChange={(e) => handleFieldChange('total_amount', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Tax Breakdown Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tax Breakdown</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">HST (Harmonized Sales Tax)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editedReceipt.tax_breakdown?.hst || ''}
                    onChange={(e) => handleFieldChange('tax_breakdown', {
                      ...editedReceipt.tax_breakdown,
                      hst: parseFloat(e.target.value) || null
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">GST (Goods & Services Tax)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editedReceipt.tax_breakdown?.gst || ''}
                    onChange={(e) => handleFieldChange('tax_breakdown', {
                      ...editedReceipt.tax_breakdown,
                      gst: parseFloat(e.target.value) || null
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">PST (Provincial Sales Tax)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editedReceipt.tax_breakdown?.pst || ''}
                    onChange={(e) => handleFieldChange('tax_breakdown', {
                      ...editedReceipt.tax_breakdown,
                      pst: parseFloat(e.target.value) || null
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">QST (Quebec Sales Tax)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editedReceipt.tax_breakdown?.qst || ''}
                    onChange={(e) => handleFieldChange('tax_breakdown', {
                      ...editedReceipt.tax_breakdown,
                      qst: parseFloat(e.target.value) || null
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Other Tax Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editedReceipt.tax_breakdown?.other_tax || ''}
                    onChange={(e) => handleFieldChange('tax_breakdown', {
                      ...editedReceipt.tax_breakdown,
                      other_tax: parseFloat(e.target.value) || null
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Other Tax Name</label>
                  <input
                    type="text"
                    value={editedReceipt.tax_breakdown?.other_tax_name || ''}
                    onChange={(e) => handleFieldChange('tax_breakdown', {
                      ...editedReceipt.tax_breakdown,
                      other_tax_name: e.target.value || null
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g., LCT, Eco Tax"
                  />
                </div>
              </div>
              
              {/* Total Tax Display */}
              <div className="mt-2">
                <label className="block text-xs text-gray-600 mb-1">Total Tax Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editedReceipt.tax_amount || ''}
                  onChange={(e) => handleFieldChange('tax_amount', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tip Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editedReceipt.tip_amount || ''}
                  onChange={(e) => handleFieldChange('tip_amount', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <input
                  type="text"
                  value={editedReceipt.payment_method || ''}
                  onChange={(e) => handleFieldChange('payment_method', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g., Cash, Card, etc."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Items Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Items ({editedReceipt.items?.length || 0})</h3>
          <Button onClick={addNewItem} variant="outline" size="sm">
            + Add Item
          </Button>
        </div>

        {editedReceipt.items && editedReceipt.items.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product Name</TableHead>
                  <TableHead>Unit Price</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Total Price</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Taxable</TableHead>
                  <TableHead>Tax Indicator</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {editedReceipt.items.map((item, index) => (
                  <TableRow key={item.id || index}>
                    <TableCell>
                      <input
                        type="text"
                        value={item.product_name || ''}
                        onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="Product name"
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        step="0.01"
                        value={item.unit_price || ''}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        step="0.01"
                        value={item.quantity || ''}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="1"
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        step="0.01"
                        value={item.total_price || ''}
                        onChange={(e) => handleItemChange(index, 'total_price', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="0.00"
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={item.category || ''}
                        onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="Groceries, Medicine..."
                      />
                    </TableCell>
                    <TableCell>
                      <select
                        value={item.is_taxable === true ? 'yes' : item.is_taxable === false ? 'no' : ''}
                        onChange={(e) => handleItemChange(index, 'is_taxable', 
                          e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null
                        )}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      >
                        <option value="">Unknown</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </TableCell>
                    <TableCell>
                      <input
                        type="text"
                        value={item.tax_indicator || ''}
                        onChange={(e) => handleItemChange(index, 'tax_indicator', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="T, *, HMRJ..."
                      />
                    </TableCell>
                    <TableCell>
                      <Button 
                        onClick={() => removeItem(index)} 
                        variant="destructive" 
                        size="sm"
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 mt-8">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={() => onSave(editedReceipt)} 
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          {loading ? 'Saving...' : 'Save Receipt'}
        </Button>
      </div>
    </div>
  );
};

// Upload Component
const ReceiptUpload = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reviewReceipt, setReviewReceipt] = useState(null);
// Camera handled by native browser API
  const navigate = useNavigate();

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    await uploadFile(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.heic'],
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const uploadFile = async (file) => {
    // Check upload quota before proceeding
    if (user && user.uploads_remaining === 0) {
      toast({
        title: "Upload Limit Reached",
        description: "You've reached your monthly upload limit. Please upgrade your subscription to continue uploading.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/receipts/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Set the receipt for review instead of navigating directly
      setReviewReceipt(response.data);
      
      // Refresh user data to update upload count
      await refreshUser();
      
      toast({
        title: "Receipt Processed!",
        description: "Please review and edit the extracted information before saving.",
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload receipt",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openCamera = () => {
    // Create a file input for camera
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'camera';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        await uploadFile(file);
      }
    };
    
    input.click();
  };

  const handleSaveReceipt = async (editedReceipt) => {
    setLoading(true);
    try {
      await axios.put(`${API}/receipts/${editedReceipt.id}`, editedReceipt);
      
      toast({
        title: "Success!",
        description: "Receipt saved successfully.",
      });
      
      navigate('/receipts');
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save receipt",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReview = () => {
    setReviewReceipt(null);
  };

  // Show review component if we have a receipt to review
  if (reviewReceipt) {
    return (
      <ReceiptReview 
        receipt={reviewReceipt}
        onSave={handleSaveReceipt}
        onCancel={handleCancelReview}
        loading={loading}
      />
    );
  }

  // Show review component if receipt is uploaded
  if (reviewReceipt) {
    return (
      <ReceiptReview 
        receipt={reviewReceipt}
        onSave={handleSaveReceipt}
        onCancel={handleCancelReview}
        loading={loading}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Receipt</h1>
        <p className="text-gray-600">Upload receipt images or PDFs and let SnapStax AI extract and organize your expense data</p>
        
        {/* AI Parsing Caution */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <div className="flex">
            <div className="text-blue-600 mr-3">ℹ️</div>
            <div>
              <h3 className="text-sm font-medium text-blue-800">AI Processing Notice</h3>
              <p className="text-sm text-blue-700 mt-1">
                After upload, SnapStax AI will extract receipt information. Please review and correct any errors before saving.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* File Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              📁 Upload File
            </CardTitle>
            <CardDescription>
              Drag and drop your receipt or click to select
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive 
                  ? 'border-emerald-400 bg-emerald-50' 
                  : 'border-gray-300 hover:border-emerald-400 hover:bg-emerald-50'
              }`}
            >
              <input {...getInputProps()} />
              <div className="space-y-4">
                <div className="text-4xl">📄</div>
                {isDragActive ? (
                  <p className="text-emerald-600 font-medium">Drop the receipt here...</p>
                ) : (
                  <>
                    <p className="text-gray-600">
                      Drag & drop your receipt here, or <span className="text-emerald-600 font-medium">browse</span>
                    </p>
                    <p className="text-sm text-gray-500">
                      Supports: JPG, PNG, HEIC, PDF
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Camera Capture */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              📸 Take Photo
            </CardTitle>
            <CardDescription>
              Use your device camera to capture receipts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={openCamera}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="camera-capture-btn"
            >
              📸 Take Photo
            </Button>
          </CardContent>
        </Card>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6">
            <div className="flex items-center space-x-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              <p className="text-lg font-medium">Processing receipt...</p>
            </div>
          </Card>
        </div>
      )}

      {/* Camera functionality handled by native browser API */}
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast({
        title: "Error",
        description: "Failed to load dashboard statistics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Prepare chart data
  const vendorData = Object.entries(stats?.spending_by_vendor || {}).map(([name, value], index) => ({
    name,
    value: parseFloat(value.toFixed(2)),
    fill: COLORS[index % COLORS.length]
  }));

  const monthlyData = Object.entries(stats?.monthly_spending || {}).map(([month, amount]) => ({
    month,
    amount: parseFloat(amount.toFixed(2))
  }));

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Overview of your SnapStax receipt tracking and spending insights powered by AI</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Receipts</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="total-receipts">
                  {stats?.total_receipts || 0}
                </p>
              </div>
              <div className="text-3xl">🧾</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Spending</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="total-spending">
                  ${(stats?.total_spending || 0).toFixed(2)}
                </p>
              </div>
              <div className="text-3xl">💰</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tax</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="total-tax">
                  ${(stats?.total_tax || 0).toFixed(2)}
                </p>
              </div>
              <div className="text-3xl">🧮</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tips</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="total-tips">
                  ${(stats?.total_tips || 0).toFixed(2)}
                </p>
              </div>
              <div className="text-3xl">💸</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscription Status */}
      {user && (
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Current Plan</p>
                <p className="text-xl font-bold text-gray-900 capitalize">
                  {user.subscription_tier} Plan
                </p>
                <p className="text-sm text-gray-500">
                  {user.uploads_remaining === -1 
                    ? 'Unlimited uploads' 
                    : `${user.uploads_remaining} uploads remaining this month`
                  }
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl mb-2">
                  {user.subscription_tier === 'free' ? '🆓' : 
                   user.subscription_tier === 'basic' ? '⚡' : '👑'}
                </div>
                <Link to="/subscription">
                  <Button variant="outline" size="sm">
                    {user.subscription_tier === 'free' ? 'Upgrade' : 'Manage'}
                  </Button>
                </Link>
              </div>
            </div>
            {user.uploads_remaining !== -1 && user.uploads_remaining <= 5 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  ⚠️ You're running low on uploads. Consider upgrading your plan for more uploads.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Monthly Spending Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Spending Trend</CardTitle>
            <CardDescription>Your spending patterns over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value}`, 'Amount']} />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Vendor Spending Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Spending by Vendor</CardTitle>
            <CardDescription>Where you spend the most</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={vendorData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: $${value}`}
                  >
                    {vendorData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value}`, 'Amount']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Quick Edit Modal Component (Simple version for basic info only)
const QuickEditModal = ({ receipt, onSave, onClose, loading }) => {
  const [editData, setEditData] = useState({
    vendor_name: '',
    date: '',
    payment_method: '',
    invoice_number: '',
    tip_amount: 0
  });

  // Update form data when receipt changes
  useEffect(() => {
    if (receipt) {
      setEditData({
        vendor_name: receipt.vendor_name || '',
        date: receipt.date || '',
        payment_method: receipt.payment_method || '',
        invoice_number: receipt.invoice_number || '',
        tip_amount: receipt.tip_amount || 0
      });
    }
  }, [receipt]);

  const handleSave = async () => {
    await onSave(editData);
  };

  return (
    <Dialog open={!!receipt} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Quick Edit Receipt</DialogTitle>
        </DialogHeader>
        {receipt && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
              <input
                type="text"
                value={editData.vendor_name}
                onChange={(e) => setEditData({...editData, vendor_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Store name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={editData.date}
                  onChange={(e) => setEditData({...editData, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                <input
                  type="text"
                  value={editData.invoice_number}
                  onChange={(e) => setEditData({...editData, invoice_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Receipt number"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={editData.payment_method}
                  onChange={(e) => setEditData({...editData, payment_method: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select method</option>
                  <option value="Cash">Cash</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Mobile Payment">Mobile Payment</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tip Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editData.tip_amount}
                  onChange={(e) => setEditData({...editData, tip_amount: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={loading} 
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Enhanced Edit Details Modal Component
const EditDetailsModal = ({ receipt, onSave, onClose, loading }) => {
  const [editData, setEditData] = useState({
    vendor_name: '',
    date: '',
    payment_method: '',
    invoice_number: '',
    tip_amount: 0,
    items: []
  });

  // Update form data when receipt changes
  useEffect(() => {
    if (receipt) {
      setEditData({
        vendor_name: receipt.vendor_name || '',
        date: receipt.date || '',
        payment_method: receipt.payment_method || '',
        invoice_number: receipt.invoice_number || '',
        tip_amount: receipt.tip_amount || 0,
        items: receipt.items ? [...receipt.items] : []
      });
    }
  }, [receipt]);

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...editData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Recalculate total_price if unit_price or quantity changes
    if (field === 'unit_price' || field === 'quantity') {
      updatedItems[index].total_price = 
        (updatedItems[index].unit_price || 0) * (updatedItems[index].quantity || 0);
    }
    
    setEditData({ ...editData, items: updatedItems });
  };

  const handleSave = async () => {
    const saveData = {
      ...editData,
      items: editData.items
    };
    await onSave(saveData);
  };

  // Calculate totals from items
  const subtotal = editData.items.reduce((sum, item) => sum + (item.total_price || 0), 0);
  const totalTax = editData.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const finalTotal = subtotal + totalTax + (editData.tip_amount || 0);

  const categoryOptions = [
    'Groceries', 'Medicine', 'Clothing', 'Electronics', 
    'Home & Garden', 'Personal Care', 'Restaurant', 'Other'
  ];

  return (
    <Dialog open={!!receipt} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Receipt Details</DialogTitle>
        </DialogHeader>
        {receipt && (
          <div className="space-y-6">
            {/* Receipt Image and Basic Info */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Receipt Image */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Original Receipt</h3>
                <div className="h-96 border rounded-lg overflow-hidden">
                  <ZoomableImage
                    src={`${BACKEND_URL}/api/receipts/${receipt.id}/file`}
                    alt="Receipt"
                    className="max-h-96 object-contain w-full"
                  />
                </div>
              </div>

              {/* Basic Info Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                  <input
                    type="text"
                    value={editData.vendor_name}
                    onChange={(e) => setEditData({...editData, vendor_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Store name"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={editData.date}
                      onChange={(e) => setEditData({...editData, date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                    <input
                      type="text"
                      value={editData.invoice_number}
                      onChange={(e) => setEditData({...editData, invoice_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Receipt number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={editData.payment_method}
                      onChange={(e) => setEditData({...editData, payment_method: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Select method</option>
                      <option value="Cash">Cash</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Mobile Payment">Mobile Payment</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tip Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editData.tip_amount}
                      onChange={(e) => setEditData({...editData, tip_amount: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Calculated Totals */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Calculated Totals</h4>
                  <div className="space-y-1 text-sm bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax:</span>
                      <span>${totalTax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tips:</span>
                      <span>${(editData.tip_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-medium border-t pt-1">
                      <span>Total:</span>
                      <span className="text-emerald-600">${finalTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Itemized Details Section */}
            {editData.items && editData.items.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Itemized Details ({editData.items.length} items)</h3>
                
                {/* Desktop Table View */}
                <div className="hidden md:block border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-96">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Unit Price</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead>Taxable</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editData.items.map((item, index) => (
                          <TableRow key={item.id || index}>
                            <TableCell>
                              <input
                                type="text"
                                value={item.product_name || ''}
                                onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                placeholder="Product name"
                              />
                            </TableCell>
                            <TableCell>
                              <select
                                value={item.category || ''}
                                onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              >
                                <option value="">Select category</option>
                                {categoryOptions.map(cat => (
                                  <option key={cat} value={cat}>{cat}</option>
                                ))}
                              </select>
                            </TableCell>
                            <TableCell>
                              <input
                                type="number"
                                step="0.01"
                                value={item.unit_price || ''}
                                onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                placeholder="0.00"
                              />
                            </TableCell>
                            <TableCell>
                              <input
                                type="number"
                                step="0.01"
                                value={item.quantity || ''}
                                onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-14 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                placeholder="1"
                              />
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium min-w-[4rem] inline-block">
                                ${(item.total_price || 0).toFixed(2)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <select
                                value={item.is_taxable === true ? 'yes' : item.is_taxable === false ? 'no' : ''}
                                onChange={(e) => handleItemChange(index, 'is_taxable', 
                                  e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null
                                )}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              >
                                <option value="">Unknown</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                              </select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {editData.items.map((item, index) => (
                    <div key={item.id || index} className="border rounded-lg p-4 bg-white">
                      <div className="space-y-3">
                        {/* Product Name */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Product Name</label>
                          <input
                            type="text"
                            value={item.product_name || ''}
                            onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            placeholder="Product name"
                          />
                        </div>

                        {/* Category */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                          <select
                            value={item.category || ''}
                            onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          >
                            <option value="">Select category</option>
                            {categoryOptions.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>

                        {/* Price, Quantity, Total in a row */}
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Unit Price</label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.unit_price || ''}
                              onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Qty</label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.quantity || ''}
                              onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              placeholder="1"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Total</label>
                            <div className="px-2 py-2 text-sm bg-gray-50 border rounded text-right font-medium">
                              ${(item.total_price || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>

                        {/* Taxable */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Taxable</label>
                          <select
                            value={item.is_taxable === true ? 'yes' : item.is_taxable === false ? 'no' : ''}
                            onChange={(e) => handleItemChange(index, 'is_taxable', 
                              e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null
                            )}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          >
                            <option value="">Unknown</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={loading} 
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Saving Changes...' : 'Save All Changes'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Receipts List Component
const ReceiptsList = () => {
  const { user } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [quickEditReceipt, setQuickEditReceipt] = useState(null);
  const [quickEditLoading, setQuickEditLoading] = useState(false);
  const [editDetailsReceipt, setEditDetailsReceipt] = useState(null);
  const [editDetailsLoading, setEditDetailsLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    try {
      const response = await axios.get(`${API}/receipts`);
      setReceipts(response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      toast({
        title: "Error",
        description: "Failed to load receipts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteReceipt = async (receiptId) => {
    try {
      await axios.delete(`${API}/receipts/${receiptId}`);
      setReceipts(receipts.filter(r => r.id !== receiptId));
      toast({
        title: "Success",
        description: "Receipt deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting receipt:', error);
      toast({
        title: "Error", 
        description: "Failed to delete receipt",
        variant: "destructive",
      });
    }
  };

  const handleQuickEdit = async (editData) => {
    setQuickEditLoading(true);
    try {
      const response = await axios.put(`${API}/receipts/${quickEditReceipt.id}/quick-edit`, editData);
      
      // Update the receipt in the list
      setReceipts(receipts.map(r => r.id === quickEditReceipt.id ? response.data : r));
      
      toast({
        title: "Success",
        description: "Receipt updated successfully",
      });
      
      setQuickEditReceipt(null);
    } catch (error) {
      console.error('Error updating receipt:', error);
      toast({
        title: "Error", 
        description: "Failed to update receipt",
        variant: "destructive",
      });
    } finally {
      setQuickEditLoading(false);
    }
  };

  const handleComprehensiveEdit = async (editData) => {
    setEditDetailsLoading(true);
    try {
      const response = await axios.put(`${API}/receipts/${editDetailsReceipt.id}/comprehensive-edit`, editData);
      
      // Update the receipt in the list
      setReceipts(receipts.map(r => r.id === editDetailsReceipt.id ? response.data : r));
      
      toast({
        title: "Success",
        description: "Receipt updated successfully",
      });
      
      setEditDetailsReceipt(null);
    } catch (error) {
      console.error('Error updating receipt:', error);
      toast({
        title: "Error", 
        description: "Failed to update receipt",
        variant: "destructive",
      });
    } finally {
      setEditDetailsLoading(false);
    }
  };

  const handleExportReceipts = async () => {
    // Check if user can export
    if (user.subscription_tier === 'free') {
      setShowUpgradeModal(true);
      return;
    }

    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/export/receipts`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipts_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Success",
        description: "Receipts exported successfully",
      });
    } catch (error) {
      console.error('Error exporting receipts:', error);
      toast({
        title: "Error", 
        description: error.response?.data?.detail || "Failed to export receipts",
        variant: "destructive",
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="receipts-list">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">All Receipts</h1>
            <p className="text-gray-600">Manage and view all your uploaded receipts</p>
          </div>
          <Button
            onClick={handleExportReceipts}
            disabled={exportLoading || receipts.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {exportLoading ? 'Exporting...' : '📥 Export to CSV'}
          </Button>
        </div>
      </div>

      {receipts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="text-6xl mb-4">📄</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No receipts yet</h3>
            <p className="text-gray-600 mb-4">Start by uploading your first receipt</p>
            <Link to="/upload">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Upload Receipt
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Tax</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receipts.map((receipt) => (
                  <TableRow key={receipt.id}>
                    <TableCell data-testid={`invoice-${receipt.id}`}>
                      {receipt.invoice_number || 'N/A'}
                    </TableCell>
                    <TableCell data-testid={`vendor-${receipt.id}`}>
                      {receipt.vendor_name || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {receipt.date || 'N/A'}
                    </TableCell>
                    <TableCell data-testid={`amount-${receipt.id}`}>
                      {receipt.total_amount ? `$${receipt.total_amount.toFixed(2)}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {receipt.tax_amount ? `$${receipt.tax_amount.toFixed(2)}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {receipt.payment_method || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedReceipt(receipt)}
                          data-testid={`view-receipt-${receipt.id}`}
                        >
                          View Details
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditDetailsReceipt(receipt)}
                          data-testid={`edit-receipt-${receipt.id}`}
                          className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        >
                          Edit Details
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteReceipt(receipt.id)}
                          data-testid={`delete-receipt-${receipt.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Receipt Details Modal */}
      <Dialog open={!!selectedReceipt} onOpenChange={() => setSelectedReceipt(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Receipt Details</DialogTitle>
          </DialogHeader>
          {selectedReceipt && (
            <div className="space-y-6">
              {/* Receipt Image and Info */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Receipt Image */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Original Receipt</h3>
                  <div className="h-96">
                    <ZoomableImage
                      src={`${BACKEND_URL}/api/receipts/${selectedReceipt.id}/file`}
                      alt="Receipt"
                      className="max-h-96 object-contain"
                    />
                  </div>
                </div>

                {/* Receipt Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Receipt Information</h3>
                  <div className="grid gap-3">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Invoice #:</span>
                      <span>{selectedReceipt.invoice_number || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Vendor:</span>
                      <span className="text-right">{selectedReceipt.vendor_name || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Date:</span>
                      <span>{selectedReceipt.date || 'N/A'}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Subtotal:</span>
                      <span>{selectedReceipt.total_amount && selectedReceipt.tax_amount ? 
                        `$${(selectedReceipt.total_amount - selectedReceipt.tax_amount).toFixed(2)}` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Tax:</span>
                      <span>{selectedReceipt.tax_amount ? `$${selectedReceipt.tax_amount.toFixed(2)}` : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Tips:</span>
                      <span>{selectedReceipt.tip_amount ? `$${selectedReceipt.tip_amount.toFixed(2)}` : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-lg border-t pt-2">
                      <span>Total Amount:</span>
                      <span className="text-emerald-600">
                        {selectedReceipt.total_amount ? `$${selectedReceipt.total_amount.toFixed(2)}` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Payment Method:</span>
                      <span>{selectedReceipt.payment_method || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items */}
              {selectedReceipt.items && selectedReceipt.items.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Itemized Details ({selectedReceipt.items.length} items)</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Quantity</TableHead>
                          <TableHead className="text-right">Total Price</TableHead>
                          <TableHead className="text-right">Tax</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedReceipt.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell>{item.category || 'N/A'}</TableCell>
                            <TableCell className="text-right">${item.unit_price.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right font-medium">${item.total_price.toFixed(2)}</TableCell>
                            <TableCell className="text-right">
                              {item.tax_amount ? `$${item.tax_amount.toFixed(2)}` : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Edit Modal */}
      <QuickEditModal
        receipt={quickEditReceipt}
        onSave={handleQuickEdit}
        onClose={() => setQuickEditReceipt(null)}
        loading={quickEditLoading}
      />

      {/* Comprehensive Edit Details Modal */}
      <EditDetailsModal
        receipt={editDetailsReceipt}
        onSave={handleComprehensiveEdit}
        onClose={() => setEditDetailsReceipt(null)}
        loading={editDetailsLoading}
      />

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade Required</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              CSV export is not available on the Free plan. Upgrade to unlock this feature!
            </p>
            <div className="bg-emerald-50 p-4 rounded-lg">
              <h4 className="font-semibold text-emerald-900 mb-2">Basic Plan - $9.99/month</h4>
              <ul className="text-sm text-emerald-700 space-y-1">
                <li>✓ 25 uploads per month</li>
                <li>✓ Receipt CSV export</li>
                <li>✓ All free features</li>
              </ul>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-purple-900 mb-2">Premium Plan - $14.99/month</h4>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>✓ Unlimited uploads</li>
                <li>✓ Receipt &amp; Items CSV export</li>
                <li>✓ All basic features</li>
              </ul>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowUpgradeModal(false);
                  window.location.href = '/subscription';
                }}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                View Plans
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Itemized Items Component
const ItemizedItems = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  
  // Batch edit states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    fetchItemizedItems();
  }, []);

  const fetchItemizedItems = async () => {
    try {
      const response = await axios.get(`${API}/items`);
      setItems(response.data.items);
    } catch (error) {
      console.error('Error fetching itemized items:', error);
      toast({
        title: "Error",
        description: "Failed to load itemized items",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedItems = React.useMemo(() => {
    let sortableItems = [...items];
    
    // Filter items
    if (filterText) {
      sortableItems = sortableItems.filter(item =>
        item.product_name.toLowerCase().includes(filterText.toLowerCase()) ||
        (item.vendor_name && item.vendor_name.toLowerCase().includes(filterText.toLowerCase()))
      );
    }
    
    // Sort items
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableItems;
  }, [items, sortConfig, filterText]);

  const getPriceTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <span className="text-red-500 text-sm">↗️ Price Up</span>;
      case 'down':
        return <span className="text-green-500 text-sm">↘️ Price Down</span>;
      case 'same':
        return <span className="text-gray-500 text-sm">→ Same Price</span>;
      case 'new':
        return <span className="text-blue-500 text-sm">✨ New Item</span>;
      default:
        return null;
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.size === sortedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(sortedItems.map(item => item.id)));
    }
  };

  const handleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleBatchUpdate = async (updates) => {
    setBatchLoading(true);
    try {
      // Group items by receipt_id
      const itemsByReceipt = {};
      sortedItems.forEach(item => {
        if (selectedItems.has(item.id)) {
          if (!itemsByReceipt[item.receipt_id]) {
            itemsByReceipt[item.receipt_id] = [];
          }
          itemsByReceipt[item.receipt_id].push(item.id);
        }
      });

      // Update each receipt's items
      const updatePromises = Object.entries(itemsByReceipt).map(([receiptId, itemIds]) => 
        axios.put(`${API}/receipts/${receiptId}/batch-update-items`, {
          item_ids: itemIds,
          updates: updates
        })
      );

      await Promise.all(updatePromises);
      
      // Refresh items
      await fetchItemizedItems();
      
      toast({
        title: "Success",
        description: `Updated ${selectedItems.size} items`,
      });
      
      // Reset selection
      setSelectedItems(new Set());
      setSelectionMode(false);
      
    } catch (error) {
      console.error('Error batch updating items:', error);
      toast({
        title: "Error",
        description: "Failed to update items",
        variant: "destructive",
      });
    } finally {
      setBatchLoading(false);
    }
  };

  const handleItemsExport = async () => {
    // Check if user can export items
    if (user.subscription_tier !== 'premium') {
      setShowUpgradeModal(true);
      return;
    }

    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/export/items`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `items_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Success",
        description: "Items exported successfully",
      });
    } catch (error) {
      console.error('Error exporting items:', error);
      toast({
        title: "Error", 
        description: error.response?.data?.detail || "Failed to export items",
        variant: "destructive",
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="itemized-items">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Itemized Items</h1>
            <p className="text-gray-600">All items from your receipts with price insights</p>
          </div>
          <Button
            onClick={handleItemsExport}
            disabled={exportLoading || items.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {exportLoading ? 'Exporting...' : '📥 Export to CSV'}
          </Button>
        </div>
      </div>

      {/* Filter and Batch Actions */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <input
          type="text"
          placeholder="Filter by product name or vendor..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg w-full max-w-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="item-filter"
        />
        
        <div className="flex gap-2">
          <Button
            onClick={() => setSelectionMode(!selectionMode)}
            variant={selectionMode ? "default" : "outline"}
            className={selectionMode ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          >
            {selectionMode ? 'Cancel Selection' : 'Batch Edit'}
          </Button>
          {selectionMode && sortedItems.length > 0 && (
            <Button
              onClick={handleSelectAll}
              variant="outline"
            >
              {selectedItems.size === sortedItems.length ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </div>
      </div>

      {sortedItems.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No items found</h3>
            <p className="text-gray-600 mb-4">
              {filterText ? 'No items match your filter criteria' : 'Upload receipts to see itemized details'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {selectionMode && (
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedItems.size === sortedItems.length && sortedItems.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300"
                        />
                      </TableHead>
                    )}
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('product_name')}
                    >
                      Product Name {sortConfig.key === 'product_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('category')}
                    >
                      Category {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('unit_price')}
                    >
                      Unit Price {sortConfig.key === 'unit_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('quantity')}
                    >
                      Quantity {sortConfig.key === 'quantity' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('total_price')}
                    >
                      Total Price {sortConfig.key === 'total_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('vendor_name')}
                    >
                      Vendor {sortConfig.key === 'vendor_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('receipt_date')}
                    >
                      Date {sortConfig.key === 'receipt_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead>Taxable</TableHead>
                    <TableHead>Tax Indicator</TableHead>
                    <TableHead>Price Trend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedItems.map((item) => (
                    <TableRow 
                      key={item.id}
                      className={selectedItems.has(item.id) ? "bg-emerald-50" : ""}
                    >
                      {selectionMode && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                            className="rounded border-gray-300"
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium" data-testid={`item-name-${item.id}`}>
                        {item.product_name}
                      </TableCell>
                      <TableCell data-testid={`item-category-${item.id}`}>
                        {item.category || 'N/A'}
                      </TableCell>
                      <TableCell data-testid={`item-price-${item.id}`}>
                        ${item.unit_price.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {item.quantity}
                      </TableCell>
                      <TableCell>
                        ${item.total_price.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {item.vendor_name || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {item.receipt_date || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {item.is_taxable === true ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            ✓ Yes
                          </span>
                        ) : item.is_taxable === false ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                            ✗ No
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">Unknown</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.tax_indicator ? (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 font-mono">
                            {item.tax_indicator}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {getPriceTrendIcon(item.price_trend)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="mt-6 text-sm text-gray-600">
        Showing {sortedItems.length} of {items.length} items
      </div>

      {/* Floating Action Bar for Batch Operations */}
      {selectionMode && selectedItems.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <Card className="px-4 py-3 shadow-lg border bg-white">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
              </span>
              
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  onClick={() => handleBatchUpdate({ is_taxable: true })}
                  disabled={batchLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Mark Taxable
                </Button>
                
                <Button
                  size="sm"
                  onClick={() => handleBatchUpdate({ is_taxable: false })}
                  disabled={batchLoading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Mark Non-Taxable
                </Button>
                
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBatchUpdate({ category: e.target.value });
                      e.target.value = '';
                    }
                  }}
                  disabled={batchLoading}
                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Set Category...</option>
                  <option value="Groceries">Groceries</option>
                  <option value="Medicine">Medicine</option>
                  <option value="Clothing">Clothing</option>
                  <option value="Electronics">Electronics</option>
                  <option value="Home & Garden">Home & Garden</option>
                  <option value="Personal Care">Personal Care</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSelectedItems(new Set());
                  setSelectionMode(false);
                }}
                disabled={batchLoading}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade Required</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Itemized CSV export is only available on the Premium plan. Upgrade to unlock this feature!
            </p>
            <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-2">Premium Plan - $14.99/month</h4>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>✓ Unlimited uploads</li>
                <li>✓ Receipt &amp; Items CSV export</li>
                <li>✓ Advanced analytics</li>
                <li>✓ Priority support</li>
              </ul>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowUpgradeModal(false);
                  window.location.href = '/subscription';
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Upgrade to Premium
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main App Content Component (authenticated)
const AppContent = () => {
  return (
    <div className="App min-h-screen bg-gray-50">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/receipts" element={<ReceiptsList />} />
          <Route path="/upload" element={<ReceiptUpload />} />
          <Route path="/items" element={<ItemizedItems />} />
          <Route path="/subscription" element={<SubscriptionManager />} />
          <Route path="/profile" element={<UserProfile />} />
        </Routes>
        <FeedbackButton />
      </BrowserRouter>
      <Toaster />
    </div>
  );
};

// User Profile Component
const UserProfile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    business_name: '',
    business_address: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        business_name: user.business_name || '',
        business_address: user.business_address || ''
      });
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(`${API}/auth/profile`, formData);
      
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      
      // Update user context
      updateUser(response.data);
      
    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({
        title: "Error",
        description: "New passwords don't match",
        variant: "destructive",
      });
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast({
        title: "Error",
        description: "Password must be at least 8 characters long",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast({
        title: "Success",
        description: "Password updated successfully",
      });
      
      // Clear password form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
    } catch (error) {
      console.error('Password update error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="user-profile">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile Settings</h1>
        <p className="text-gray-600">Manage your account information and business details</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span>👤</span>
              Profile Information
            </CardTitle>
            <CardDescription>
              Update your personal and business information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name
                </label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Your business name (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Address
                </label>
                <textarea
                  rows="3"
                  value={formData.business_address}
                  onChange={(e) => setFormData({...formData, business_address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Business address (optional)"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span>🔒</span>
              Change Password
            </CardTitle>
            <CardDescription>
              Update your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password *
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password *
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  required
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Confirm new password"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Password Requirements:</span><br/>
                  • At least 8 characters long<br/>
                  • Use a strong, unique password
                </p>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

// Main App Router
const MainApp = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <div className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent mb-2">
            SnapStax
          </div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <AppContent /> : <Auth />;
};

export default App;